import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { clip } from '../../data/cameras.js';
import {
    clipFailed, headOf, hms, markClipFailed, seedOf, useInView, usePageVisible, usePlaySlot, ymd
} from './media.js';

/* How long a clip may take to produce a frame before the feed gives up
   on it and stays on the still. */
const CLIP_TIMEOUT = 5000;

const CENTER = { x: 0, y: 0 };

/**
 * The picture of one camera: its clip when the clip will play, and its
 * still, drifting slowly, when it will not.
 *
 * The still (a frame of the same clip) is always underneath. The video
 * only fades in once it has a frame, so a clip that is loading shows its
 * own first frame and the swap is a dissolve rather than a flash.
 *
 * Only a clip that fails gets the slow Ken Burns drift, which keeps a
 * photograph from reading as a frozen feed. An error is final for the
 * session; a clip that is merely slow drifts while it keeps loading, and
 * takes over the moment it has a frame.
 *
 * The video is not mounted at all while the feed is off screen, the page
 * is hidden, or the decode budget is spent, which is what stops a wall
 * from downloading every clip in the building.
 *
 * `zoom` and `pan` (percent of the frame) are the viewer's digital zoom
 * and PTZ; `playback` holds the feed on a point in the recording.
 * The ref exposes capture(), a JPEG of what is on screen.
 */
const CameraFeed = forwardRef(function CameraFeed(
    { camera, active = true, priority = 0, playback = null, zoom = 1, pan = CENTER, smooth = false },
    ref
) {
    const box = useRef(null);
    const video = useRef(null);
    const still = useRef(null);
    const inView = useInView(box);
    const pageVisible = usePageVisible();

    const [failed, setFailed] = useState(() => clipFailed(camera.key));
    const [stalled, setStalled] = useState(false);
    const [ready, setReady] = useState(false);
    const slot = usePlaySlot(active && inView && pageVisible && !failed, priority);
    const showVideo = slot && !failed;
    const sources = clip(camera.key);

    const fail = useCallback(() => {
        markClipFailed(camera.key);
        setFailed(true);
        setReady(false);
    }, [camera.key]);

    useEffect(() => { if (!showVideo) setReady(false); }, [showVideo]);

    /* No frame after a few seconds shows the fallback too (a server that
       answers a missing clip with the app's own index.html never errors
       cleanly, it just never plays), without giving up on the clip. */
    useEffect(() => {
        if (!showVideo || ready) return undefined;
        const timer = setTimeout(() => setStalled(true), CLIP_TIMEOUT);
        return () => clearTimeout(timer);
    }, [showVideo, ready]);

    /* Playback: seek when the playhead is placed, hold when paused. The
       clips are short loops, so the recording's timeline is mapped onto
       the clip by wrapping. */
    const seekKey = playback && playback.seek ? playback.setAt : 0;
    const held = !!(playback && playback.paused);
    useEffect(() => {
        const el = video.current;
        if (!el || !ready) return;
        if (seekKey) {
            const length = el.duration;
            if (Number.isFinite(length) && length > 0) {
                try { el.currentTime = (playback.at / 1000) % length; } catch (error) { /* not seekable yet */ }
            }
        }
        if (held) {
            if (el.pause) el.pause();
        } else if (el.play) {
            const pending = el.play();
            if (pending && pending.catch) pending.catch(() => {});
        }
        // `playback` is read through seekKey and held on purpose: a new
        // object with the same position must not seek again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seekKey, held, ready]);

    /* capture() returns { src, at }: the JPEG and the moment it shows,
       which is the playhead's time in playback, so the stamp burned
       into the frame and the record kept beside it always agree. */
    useImperativeHandle(ref, () => ({
        capture: () => {
            const at = headOf(playback, Date.now());
            const src = captureFrame({
                video: ready ? video.current : null,
                image: still.current,
                zoom,
                pan,
                camera,
                at,
                playback: !!playback
            });
            return src ? { src, at } : null;
        }
    }), [ready, zoom, pan, camera, playback]);

    /* Every camera drifts on its own timing, so a wall of stills never
       moves in step. */
    const seed = seedOf(camera.id);
    const style = {
        '--drift-dur': `${22 + (seed % 9)}s`,
        '--drift-delay': `-${seed % 17}s`,
        '--drift-origin': `${30 + (seed % 40)}% ${35 + ((seed >> 3) % 30)}%`
    };

    const frame = { transform: `translate3d(${pan.x}%, ${pan.y}%, 0) scale(${zoom})` };

    const classes = ['cam-feed'];
    if (ready) classes.push('cam-feed--video');
    if (failed || (stalled && !ready)) classes.push('cam-feed--fallback');
    if (playback) classes.push('cam-feed--playback');
    if (smooth) classes.push('cam-feed--smooth');

    return (
        <div ref={box} className={classes.join(' ')} style={style}>
            <div className="cam-feed__frame" style={frame}>
                <img
                    ref={still}
                    className="cam-feed__still"
                    src={camera.still}
                    alt=""
                    draggable={false}
                    decoding="async"
                />
                {showVideo && (
                    <video
                        ref={el => {
                            video.current = el;
                            if (el) { el.muted = true; el.defaultMuted = true; }
                        }}
                        className="cam-feed__video"
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="metadata"
                        poster={sources.poster}
                        disablePictureInPicture
                        onLoadedData={() => { setReady(true); setStalled(false); }}
                        onError={fail}
                    >
                        {sources.webm && <source src={sources.webm} type="video/webm" />}
                        <source src={sources.mp4} type="video/mp4" onError={fail} />
                    </video>
                )}
            </div>
        </div>
    );
});

export default CameraFeed;

/* ─────────────────────────────────────────────────────────────
   A snapshot: the frame on screen, cropped to the viewer's zoom and pan,
   graded like the live picture and stamped the way a recorder stamps
   an export, so a still pulled into an incident report says where and
   when it is from on its own.

   Returns null wherever a canvas cannot be drawn (a test renderer, a
   tainted cross-origin frame); the caller says so instead.
   ───────────────────────────────────────────────────────────── */
export function captureFrame({ video, image, zoom = 1, pan = CENTER, camera, at, playback }) {
    if (typeof document === 'undefined') return null;

    const usable = video && video.readyState >= 2 && video.videoWidth;
    const source = usable ? video : image;
    if (!source) return null;
    const sw = source.videoWidth || source.naturalWidth;
    const sh = source.videoHeight || source.naturalHeight;
    if (!sw || !sh) return null;

    const canvas = document.createElement('canvas');
    let ctx = null;
    try { ctx = canvas.getContext && canvas.getContext('2d'); } catch (error) { ctx = null; }
    if (!ctx) return null;

    const W = 1280;
    const H = 720;
    canvas.width = W;
    canvas.height = H;

    /* object-fit: cover, then the part of it the zoom and pan show. */
    const cover = Math.max(W / sw, H / sh);
    const cw = W / cover;
    const ch = H / cover;
    const cx = (sw - cw) / 2;
    const cy = (sh - ch) / 2;
    const fx = 0.5 - (0.5 + pan.x / 100) / zoom;
    const fy = 0.5 - (0.5 + pan.y / 100) / zoom;

    try {
        ctx.filter = 'saturate(0.85) contrast(1.06)';
        ctx.drawImage(source, cx + fx * cw, cy + fy * ch, cw / zoom, ch / zoom, 0, 0, W, H);
        ctx.filter = 'none';
    } catch (error) {
        return null;
    }

    const vignette = ctx.createRadialGradient(W / 2, H / 2, H * 0.4, W / 2, H / 2, H * 1.05);
    vignette.addColorStop(0, 'rgba(0,0,0,0)');
    vignette.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);

    const band = ctx.createLinearGradient(0, H - 150, 0, H);
    band.addColorStop(0, 'rgba(0,0,0,0)');
    band.addColorStop(1, 'rgba(0,0,0,0.78)');
    ctx.fillStyle = band;
    ctx.fillRect(0, H - 150, W, 150);

    const font = 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f5f5f7';
    ctx.font = `700 30px ${font}`;
    ctx.fillText(camera.name, 36, H - 62);
    ctx.fillStyle = 'rgba(245,245,247,0.72)';
    ctx.font = `500 20px ${font}`;
    ctx.fillText(`${camera.location}  ·  ${camera.id.toUpperCase()}${zoom > 1 ? `  ·  ${zoom.toFixed(1)}x` : ''}`, 36, H - 30);

    ctx.textAlign = 'right';
    ctx.fillStyle = '#f5f5f7';
    ctx.font = `600 26px ${font}`;
    ctx.fillText(`${ymd(at)}  ${hms(at)}`, W - 36, H - 30);

    /* The mark that says this is an export, in the accent. */
    const mark = playback ? 'SNAPSHOT  ·  PLAYBACK' : 'SNAPSHOT  ·  LIVE';
    ctx.textAlign = 'left';
    ctx.font = `700 17px ${font}`;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(28, 26, ctx.measureText(mark).width + 28, 40);
    ctx.fillStyle = '#A3E7FC';
    ctx.fillText(mark, 42, 52);

    try {
        return canvas.toDataURL('image/jpeg', 0.88);
    } catch (error) {
        return null;
    }
}
